export type CampaignStatus =
  | "draft"
  | "archived"
  | "not-started"
  | "active"
  | "finished"
  | "unlimited";

type CampaignWithStatus = {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  status?: string;
};

/**
 * Calculate campaign display status based on lifecycle status and dates.
 * Lifecycle status (Draft/Published/Archived) takes priority.
 * Date sub-statuses only apply to Published campaigns.
 */
export const calculateCampaignStatus = (
  campaign: CampaignWithStatus,
): CampaignStatus => {
  const { status } = campaign;
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (status === "Draft") {
    return "draft";
  }

  if (status === "Archived") {
    return "archived";
  }

  // Published campaigns — calculate from dates
  if (!campaign.startDate && !campaign.endDate) {
    return "unlimited";
  }

  const startDate = campaign.startDate ? new Date(campaign.startDate) : null;
  const endDate = campaign.endDate ? new Date(campaign.endDate) : null;

  if (startDate) {
    startDate.setHours(0, 0, 0, 0);
  }
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
  }

  if (startDate && now < startDate) {
    return "not-started";
  }

  if (endDate && now > endDate) {
    return "finished";
  }

  return "active";
};

/**
 * Check if a campaign is currently active (running or unlimited)
 */
export const isCampaignActive = (campaign: CampaignWithStatus): boolean => {
  const status = calculateCampaignStatus(campaign);
  return status === "active" || status === "unlimited";
};

/**
 * Calculate days left until a date from today (for end dates)
 */
export const getDaysLeftUntilDate = (date: Date | string | null): number => {
  if (!date) {
    return 0;
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const targetDate = new Date(date);
  targetDate.setHours(23, 59, 59, 999);

  return Math.ceil(
    (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
};
